// eslint-disable-next-line no-restricted-imports
import { ActionType } from "@src/app/store/session";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";

import { type ApiResponse, post } from "../../api/api";
import { type IdentifyProvider, IdentifyProviderEnum } from "./types";
import {
  VALIDATE_OTP_PATH,
  ValidateOtpError,
  type ValidateOtpRequest,
  validateOtpRequestSchema,
  type ValidateOtpResponse,
  validateOtpResponseSchema,
} from "./useValidateOtp";

interface ValidateAuthOtpInput extends ValidateOtpRequest {
  identityProvider: IdentifyProvider;
}

export function useValidateAuthOtp(
  options: UseMutationOptions<
    ApiResponse<ValidateOtpResponse>,
    AxiosError,
    ValidateAuthOtpInput
  > = {}
): UseMutationResult<ApiResponse<ValidateOtpResponse>, AxiosError, ValidateAuthOtpInput> {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (input: ValidateAuthOtpInput) => {
      const { identityProvider, isSignup, otp, phone } = input;

      // Cognito for signup (onboarding)
      if (identityProvider === IdentifyProviderEnum.cognito && isSignup) {
        const cognitoAuth = getCognitoSingleton();

        try {
          await cognitoAuth.confirmSignUp(otp);
        } catch (error) {
          logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
            error,
            metadata: { identityProvider, phone, isSignup },
          });

          const cognitoError = error as { name?: string };
          const isWrongOtp =
            cognitoError.name === "CodeMismatchException" ||
            cognitoError.name === "ExpiredCodeException";

          if (!isWrongOtp) {
            throw error;
          }

          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "Invalid OTP",
              error: ValidateOtpError.INVALID_OTP,
            },
          } satisfies ApiResponse<ValidateOtpResponse>;
        }

        /**
         * For Cognito signups, populate the Redux session state with the phone number
         * so the onboarding flow has access to it.
         */
        dispatch({
          type: ActionType.LOGGED_IN,
          data: {
            profile: {
              phone: `+1${phone}`,
            },
          },
        });

        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            data: {
              agentDeletionRequestCancelled: false,
            },
          },
        } as ApiResponse<ValidateOtpResponse>;
      }

      // Cognito for login (phone OTP)
      if (identityProvider === IdentifyProviderEnum.cognito && !isSignup) {
        const cognitoAuth = getCognitoSingleton();

        try {
          await cognitoAuth.confirmPhoneOtp(otp);
        } catch (error) {
          logError(APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE, {
            error,
            metadata: { identityProvider, phone },
          });

          const cognitoError = error as { name?: string; code?: string };
          const isWrongOtp =
            cognitoError.name === "CodeMismatchException" ||
            cognitoError.name === "ExpiredCodeException" ||
            cognitoError.code === "auth/wrong-password" ||
            cognitoError.code === "auth/code-expired" ||
            cognitoError.code === "auth/challenge-error";

          if (!isWrongOtp) {
            throw error;
          }

          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "Invalid OTP",
              error: ValidateOtpError.INVALID_OTP,
            },
          } satisfies ApiResponse<ValidateOtpResponse>;
        }

        // Cognito session is now established — useIsAuthenticated will react.
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            data: {
              agentDeletionRequestCancelled: false,
            },
          },
        } as ApiResponse<ValidateOtpResponse>;
      }

      return await post({
        url: VALIDATE_OTP_PATH,
        data: { isSignup, otp, phone },
        requestSchema: validateOtpRequestSchema,
        responseSchema: validateOtpResponseSchema,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.VALIDATE_OTP_FAILURE,
    },
    ...options,
  });
}
